import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Clock,
  SignIn,
  SignOut,
  CalendarBlank,
  UsersThree,
  Plus,
  Trash,
  Hourglass,
  PencilSimple,
} from '@phosphor-icons/react';
import { useApp } from '@/context/AppContext';
import { useAuth } from '@/context/AuthContext';
import { useTime } from '@/context/TimeContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { API_URL as __API_BASE__ } from '@/lib/apiBase';
const API_URL = __API_BASE__;

const ROLES = [
  'cashier', 'server', 'kitchen_staff', 'expediter',
  'inventory_manager', 'outlet_manager',
];

const fmtMin = (m) => `${Math.floor(m / 60)}h ${m % 60}m`;
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString() : '—');
const fmtTimeShort = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—');
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');

const todayISO = () => new Date().toISOString().slice(0, 10);
const sevenDaysAgo = () => new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10);

const TimesheetPage = () => {
  const { currentOutlet } = useApp();
  const { user } = useAuth();
  const { current, clockIn: ctxClockIn, clockOut: ctxClockOut } = useTime();
  const isManager = ['super_admin', 'company_admin', 'outlet_manager'].includes(user?.role);

  const [myEntries, setMyEntries] = useState([]);
  const [teamEntries, setTeamEntries] = useState([]);
  const [summary, setSummary] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [range, setRange] = useState({ start: sevenDaysAgo(), end: todayISO() });

  const [showShiftDialog, setShowShiftDialog] = useState(false);
  const [activeEntries, setActiveEntries] = useState([]); // who is currently on the clock
  const [busyUserId, setBusyUserId] = useState(null);

  // Time-entry edit/create dialog (managers only)
  const [entryDialog, setEntryDialog] = useState(null); // { mode: 'edit'|'new', data: {...} }
  const [shiftForm, setShiftForm] = useState({
    user_id: '',
    date: todayISO(),
    start: '09:00',
    end: '17:00',
    role: 'cashier',
    notes: '',
  });

  const fetchAll = useCallback(async () => {
    if (!currentOutlet) return;
    try {
      const calls = [
        axios.get(`${API_URL}/api/time/entries?outlet_id=${currentOutlet.id}&user_id=${user.id}&start=${range.start}&end=${range.end}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/shifts?outlet_id=${currentOutlet.id}&start=${range.start}&end=${range.end}`, { withCredentials: true }),
      ];
      if (isManager) {
        calls.push(axios.get(`${API_URL}/api/time/entries?outlet_id=${currentOutlet.id}&start=${range.start}&end=${range.end}`, { withCredentials: true }));
        calls.push(axios.get(`${API_URL}/api/time/summary?outlet_id=${currentOutlet.id}&start=${range.start}&end=${range.end}`, { withCredentials: true }));
        calls.push(axios.get(`${API_URL}/api/users`, { withCredentials: true }));
        calls.push(axios.get(`${API_URL}/api/time/active?outlet_id=${currentOutlet.id}`, { withCredentials: true }));
      }
      const responses = await Promise.all(calls);
      setMyEntries(responses[0].data);
      setShifts(responses[1].data);
      if (isManager) {
        setTeamEntries(responses[2].data);
        setSummary(responses[3].data);
        setStaff(responses[4].data);
        setActiveEntries(responses[5].data || []);
      }
    } catch (e) { console.error(e); }
  }, [currentOutlet, range, isManager, user?.id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Live re-render for the timer card
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!current?.clocked_in) return;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [current?.clocked_in]);

  const elapsed = current?.clocked_in
    ? Math.max(0, Math.floor((Date.now() - new Date(current.entry.clock_in).getTime()) / 1000))
    : 0;
  const elapsedStr = `${String(Math.floor(elapsed / 3600)).padStart(2, '0')}:${String(Math.floor((elapsed % 3600) / 60)).padStart(2, '0')}:${String(elapsed % 60).padStart(2, '0')}`;

  const clockIn = async () => {
    const ok = await ctxClockIn(currentOutlet?.id);
    if (ok) fetchAll();
  };

  const clockOut = async () => {
    const ok = await ctxClockOut();
    if (ok) fetchAll();
  };

  // Manager actions: clock another employee in/out
  const isUserOnClock = (uid) => activeEntries.some((a) => a.user_id === uid);
  const activeEntryFor = (uid) => activeEntries.find((a) => a.user_id === uid);

  const adminClockIn = async (u) => {
    if (busyUserId) return;
    setBusyUserId(u._id);
    try {
      await axios.post(
        `${API_URL}/api/time/clock-in`,
        { outlet_id: currentOutlet.id, user_id: u._id },
        { withCredentials: true },
      );
      toast.success(`${u.name} clocked in`);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to clock in');
    } finally {
      setBusyUserId(null);
    }
  };

  const adminClockOut = async (u) => {
    if (busyUserId) return;
    setBusyUserId(u._id);
    try {
      await axios.post(
        `${API_URL}/api/time/clock-out`,
        { user_id: u._id },
        { withCredentials: true },
      );
      toast.success(`${u.name} clocked out`);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to clock out');
    } finally {
      setBusyUserId(null);
    }
  };

  // ---- Manual time-entry CRUD (managers) ----
  const toLocalInputValue = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const openEditEntry = (entry) => {
    setEntryDialog({
      mode: 'edit',
      data: {
        id: entry.id || entry._id,
        user_id: entry.user_id,
        user_name: entry.user_name,
        clock_in: toLocalInputValue(entry.clock_in),
        clock_out: toLocalInputValue(entry.clock_out),
        break_minutes: entry.break_minutes || 0,
        notes: entry.notes || '',
      },
    });
  };

  const openNewEntry = () => {
    const now = new Date();
    const start = new Date(now.getTime() - 60 * 60 * 1000);
    setEntryDialog({
      mode: 'new',
      data: {
        user_id: '',
        clock_in: toLocalInputValue(start.toISOString()),
        clock_out: toLocalInputValue(now.toISOString()),
        break_minutes: 0,
        notes: '',
      },
    });
  };

  const saveEntry = async () => {
    const d = entryDialog.data;
    if (!d.user_id) { toast.error('Pick a staff member'); return; }
    if (!d.clock_in) { toast.error('Clock In is required'); return; }
    const payload = {
      user_id: d.user_id,
      outlet_id: currentOutlet.id,
      clock_in: new Date(d.clock_in).toISOString(),
      clock_out: d.clock_out ? new Date(d.clock_out).toISOString() : null,
      break_minutes: parseInt(d.break_minutes, 10) || 0,
      notes: d.notes || '',
    };
    try {
      if (entryDialog.mode === 'edit') {
        await axios.patch(`${API_URL}/api/time/entries/${d.id}`, payload, { withCredentials: true });
        toast.success('Time entry updated');
      } else {
        await axios.post(`${API_URL}/api/time/entries`, payload, { withCredentials: true });
        toast.success('Time entry added');
      }
      setEntryDialog(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    }
  };

  const deleteEntry = async (entry) => {
    if (!window.confirm('Delete this time entry? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/time/entries/${entry.id || entry._id}`, { withCredentials: true });
      toast.success('Time entry deleted');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const createShift = async () => {
    if (!shiftForm.user_id) { toast.error('Pick a staff member'); return; }
    try {
      const start_time = `${shiftForm.date}T${shiftForm.start}:00Z`;
      const end_time = `${shiftForm.date}T${shiftForm.end}:00Z`;
      await axios.post(`${API_URL}/api/shifts`, {
        user_id: shiftForm.user_id,
        outlet_id: currentOutlet.id,
        start_time, end_time,
        role: shiftForm.role,
        notes: shiftForm.notes,
      }, { withCredentials: true });
      toast.success('Shift scheduled');
      setShowShiftDialog(false);
      setShiftForm({ ...shiftForm, user_id: '', notes: '' });
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const deleteShift = async (id) => {
    if (!window.confirm('Delete this shift?')) return;
    try {
      await axios.delete(`${API_URL}/api/shifts/${id}`, { withCredentials: true });
      toast.success('Shift deleted');
      fetchAll();
    } catch (e) { toast.error('Failed'); }
  };

  if (!currentOutlet) {
    return <p className="text-muted-foreground text-center py-12">Select an outlet to view timesheets.</p>;
  }

  return (
    <div className="space-y-6" data-testid="timesheet-page">
      <div>
        <h1 className="font-heading text-3xl font-bold">Time & Shifts</h1>
        <p className="text-muted-foreground mt-1">Clock in/out, view your hours, and manage staff shifts.</p>
      </div>

      {/* Current timer card */}
      <Card
        className="border-2"
        style={{
          borderColor: current?.clocked_in ? 'rgba(34,197,94,0.5)' : 'var(--theme-border)',
          backgroundColor: current?.clocked_in ? 'rgba(34,197,94,0.06)' : 'var(--theme-surface)',
        }}
        data-testid="clock-card"
      >
        <CardContent className="p-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{
                backgroundColor: current?.clocked_in ? 'rgba(34,197,94,0.15)' : 'var(--theme-background)',
                color: current?.clocked_in ? '#16a34a' : 'var(--theme-text-muted)',
              }}
            >
              {current?.clocked_in ? <Hourglass size={32} weight="duotone" /> : <Clock size={32} />}
            </div>
            <div>
              <p className="text-sm text-[var(--theme-text-muted)]">
                {current?.clocked_in ? 'Currently clocked in' : 'Not clocked in'}
              </p>
              {current?.clocked_in ? (
                <>
                  <p className="text-4xl font-mono font-bold tabular-nums tracking-tight">{elapsedStr}</p>
                  <p className="text-xs text-[var(--theme-text-muted)] mt-1">
                    Since {fmtTime(current.entry?.clock_in)}
                  </p>
                </>
              ) : (
                <p className="text-2xl font-bold">{user?.name}</p>
              )}
            </div>
          </div>
          {current?.clocked_in ? (
            <Button onClick={clockOut} className="bg-red-500 hover:bg-red-600 text-white h-12 px-6" data-testid="clock-out-btn">
              <SignOut size={20} className="mr-2" /> Clock Out
            </Button>
          ) : (
            <Button onClick={clockIn} className="bg-green-500 hover:bg-green-600 text-white h-12 px-6" data-testid="clock-in-btn">
              <SignIn size={20} className="mr-2" /> Clock In
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue={isManager ? 'team' : 'me'} className="space-y-4">
        <TabsList>
          <TabsTrigger value="me" data-testid="tab-me">My Time</TabsTrigger>
          {isManager && <TabsTrigger value="team" data-testid="tab-team">Team Timesheet</TabsTrigger>}
          <TabsTrigger value="schedule" data-testid="tab-schedule">Schedule</TabsTrigger>
        </TabsList>

        {/* Date range */}
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="range-start" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="range-end" />
          </div>
        </div>

        {/* My Time */}
        <TabsContent value="me">
          <Card>
            <CardHeader><CardTitle className="text-lg">Your time entries</CardTitle></CardHeader>
            <CardContent>
              <table className="w-full text-sm" data-testid="my-entries-table">
                <thead>
                  <tr className="text-left text-xs uppercase text-[var(--theme-text-muted)] border-b">
                    <th className="py-2">Date</th>
                    <th>Clock In</th>
                    <th>Clock Out</th>
                    <th>Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {myEntries.map((e) => (
                    <tr key={e._id || e.id} className="border-b border-[var(--theme-border)]/50">
                      <td className="py-2">{fmtDate(e.clock_in)}</td>
                      <td>{fmtTimeShort(e.clock_in)}</td>
                      <td>{fmtTimeShort(e.clock_out)}</td>
                      <td className="font-mono">{e.clock_out ? fmtMin(e.total_minutes || 0) : <span className="text-green-600">In progress</span>}</td>
                    </tr>
                  ))}
                  {myEntries.length === 0 && (
                    <tr><td colSpan="4" className="py-6 text-center text-[var(--theme-text-muted)]">No entries in this range.</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Team Timesheet */}
        {isManager && (
          <TabsContent value="team">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <UsersThree size={20} /> Hours by staff
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="team-summary">
                  {staff.map((u) => {
                    const s = summary.find((x) => x.user_id === u._id) || {};
                    const hasShifts = (s.shifts || 0) > 0;
                    const onClock = isUserOnClock(u._id);
                    const liveEntry = activeEntryFor(u._id);
                    return (
                      <div
                        key={u._id}
                        className={`rounded-xl border p-4 flex flex-col gap-3 transition-colors ${
                          onClock
                            ? 'border-green-500/60 bg-green-500/5'
                            : hasShifts
                              ? 'border-[var(--theme-border)]'
                              : 'border-dashed border-[var(--theme-border)] opacity-80'
                        }`}
                        data-testid={`staff-card-${u._id}`}
                      >
                        <div>
                          <p className="text-xs text-[var(--theme-text-muted)] uppercase tracking-wider truncate">{u.name}</p>
                          <p className="text-[10px] text-[var(--theme-text-muted)] capitalize">{u.role?.replace(/_/g, ' ')}</p>
                          <p className="text-3xl font-bold font-mono tabular-nums mt-1">{fmtMin(s.total_minutes || 0)}</p>
                          <p className="text-xs text-[var(--theme-text-muted)] mt-1">
                            {(s.shifts || 0)} shift{(s.shifts || 0) !== 1 ? 's' : ''}
                            {onClock && liveEntry && (
                              <span className="ml-2 inline-flex items-center gap-1 text-green-700 font-medium">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                                On clock since {fmtTimeShort(liveEntry.clock_in)}
                              </span>
                            )}
                          </p>
                        </div>
                        {onClock ? (
                          <Button
                            size="sm"
                            disabled={busyUserId === u._id}
                            onClick={() => adminClockOut(u)}
                            className="bg-red-500 hover:bg-red-600 text-white"
                            data-testid={`admin-clock-out-${u._id}`}
                          >
                            <SignOut size={14} className="mr-1.5" />
                            {busyUserId === u._id ? 'Clocking out…' : 'Clock Out'}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busyUserId === u._id}
                            onClick={() => adminClockIn(u)}
                            className="border-green-500 text-green-700 hover:bg-green-50"
                            data-testid={`admin-clock-in-${u._id}`}
                          >
                            <SignIn size={14} className="mr-1.5" />
                            {busyUserId === u._id ? 'Clocking in…' : 'Clock In'}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                  {staff.length === 0 && <p className="text-sm text-[var(--theme-text-muted)] col-span-full">No employees configured for this outlet.</p>}
                </div>

                <div className="border-t border-[var(--theme-border)] pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium">Recent entries</p>
                    <Button size="sm" onClick={openNewEntry} className="bg-[var(--theme-accent)] text-white" data-testid="add-entry-btn">
                      <Plus size={14} className="mr-1" /> Add entry
                    </Button>
                  </div>
                  <table className="w-full text-sm" data-testid="team-entries-table">
                    <thead>
                      <tr className="text-left text-xs uppercase text-[var(--theme-text-muted)] border-b">
                        <th className="py-2">Staff</th>
                        <th>Date</th>
                        <th>In</th>
                        <th>Out</th>
                        <th>Break</th>
                        <th>Hours</th>
                        <th className="text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teamEntries.slice(0, 30).map((e) => (
                        <tr key={e._id || e.id} className="border-b border-[var(--theme-border)]/50" data-testid={`entry-row-${e.id || e._id}`}>
                          <td className="py-2">
                            {e.user_name || '—'}
                            {e.manual && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">manual</span>}
                            {e.edited_by && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">edited</span>}
                          </td>
                          <td>{fmtDate(e.clock_in)}</td>
                          <td>{fmtTimeShort(e.clock_in)}</td>
                          <td>{fmtTimeShort(e.clock_out)}</td>
                          <td className="text-xs text-[var(--theme-text-muted)]">{e.break_minutes ? `${e.break_minutes}m` : '—'}</td>
                          <td className="font-mono">{e.clock_out ? fmtMin(e.total_minutes || 0) : <span className="text-green-600">In progress</span>}</td>
                          <td className="text-right whitespace-nowrap">
                            <button
                              onClick={() => openEditEntry(e)}
                              className="p-1.5 rounded-md hover:bg-[var(--theme-background)] text-[var(--theme-text-muted)] hover:text-[var(--theme-accent)]"
                              title="Edit entry"
                              data-testid={`entry-edit-${e.id || e._id}`}
                            >
                              <PencilSimple size={14} />
                            </button>
                            <button
                              onClick={() => deleteEntry(e)}
                              className="p-1.5 rounded-md hover:bg-red-50 text-[var(--theme-text-muted)] hover:text-red-500"
                              title="Delete entry"
                              data-testid={`entry-delete-${e.id || e._id}`}
                            >
                              <Trash size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                      {teamEntries.length === 0 && (
                        <tr><td colSpan="7" className="py-6 text-center text-[var(--theme-text-muted)]">No entries.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Schedule */}
        <TabsContent value="schedule">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <CalendarBlank size={20} /> Upcoming shifts
              </CardTitle>
              {isManager && (
                <Button size="sm" onClick={() => setShowShiftDialog(true)} className="bg-[var(--theme-accent)] text-white" data-testid="new-shift-btn">
                  <Plus size={14} className="mr-1" /> New Shift
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="space-y-2" data-testid="shifts-list">
                {shifts.map((s) => (
                  <div key={s._id || s.id} className="flex items-center justify-between p-3 rounded-xl border border-[var(--theme-border)]" data-testid={`shift-${s._id || s.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">{s.user_name || 'Unknown'}</p>
                      <p className="text-xs text-[var(--theme-text-muted)]">
                        {fmtDate(s.start_time)} · {fmtTimeShort(s.start_time)} → {fmtTimeShort(s.end_time)}
                        {s.role ? ` · ${s.role.replace('_', ' ')}` : ''}
                      </p>
                    </div>
                    <span className="px-2 py-0.5 text-xs rounded-full bg-blue-500/10 text-blue-600 border border-blue-500/30 capitalize mr-3">
                      {s.status || 'scheduled'}
                    </span>
                    {isManager && (
                      <button onClick={() => deleteShift(s._id || s.id)} className="text-[var(--theme-text-muted)] hover:text-red-500 p-1" data-testid={`shift-delete-${s._id || s.id}`}>
                        <Trash size={14} />
                      </button>
                    )}
                  </div>
                ))}
                {shifts.length === 0 && (
                  <p className="text-sm text-[var(--theme-text-muted)] text-center py-6">No shifts scheduled for this range.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* New shift dialog */}
      <Dialog open={showShiftDialog} onOpenChange={setShowShiftDialog}>        <DialogContent data-testid="shift-dialog">
          <DialogHeader><DialogTitle>Schedule a shift</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Staff</Label>
              <Select value={shiftForm.user_id} onValueChange={(v) => setShiftForm({ ...shiftForm, user_id: v })}>
                <SelectTrigger data-testid="shift-user-select"><SelectValue placeholder="Select staff" /></SelectTrigger>
                <SelectContent>
                  {staff.map((u) => (
                    <SelectItem key={u._id} value={u._id}>{u.name} · {u.role?.replace('_', ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={shiftForm.date} onChange={(e) => setShiftForm({ ...shiftForm, date: e.target.value })} data-testid="shift-date" />
              </div>
              <div>
                <Label className="text-xs">Start</Label>
                <Input type="time" value={shiftForm.start} onChange={(e) => setShiftForm({ ...shiftForm, start: e.target.value })} data-testid="shift-start" />
              </div>
              <div>
                <Label className="text-xs">End</Label>
                <Input type="time" value={shiftForm.end} onChange={(e) => setShiftForm({ ...shiftForm, end: e.target.value })} data-testid="shift-end" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Role</Label>
              <Select value={shiftForm.role} onValueChange={(v) => setShiftForm({ ...shiftForm, role: v })}>
                <SelectTrigger data-testid="shift-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r} value={r}>{r.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Input value={shiftForm.notes} onChange={(e) => setShiftForm({ ...shiftForm, notes: e.target.value })} data-testid="shift-notes" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => setShowShiftDialog(false)}>Cancel</Button>
            <Button onClick={createShift} className="flex-1 bg-[var(--theme-accent)] text-white" data-testid="shift-save-btn">Schedule</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit / Add time entry dialog (managers only) */}
      <Dialog open={!!entryDialog} onOpenChange={(open) => !open && setEntryDialog(null)}>
        <DialogContent data-testid="entry-dialog">
          <DialogHeader>
            <DialogTitle>{entryDialog?.mode === 'edit' ? 'Edit time entry' : 'Add time entry'}</DialogTitle>
          </DialogHeader>
          {entryDialog && (
            <div className="space-y-3 py-2">
              {entryDialog.mode === 'new' ? (
                <div>
                  <Label className="text-xs">Staff</Label>
                  <Select value={entryDialog.data.user_id} onValueChange={(v) => setEntryDialog({ ...entryDialog, data: { ...entryDialog.data, user_id: v } })}>
                    <SelectTrigger data-testid="entry-user-select"><SelectValue placeholder="Select staff" /></SelectTrigger>
                    <SelectContent>
                      {staff.map((u) => (
                        <SelectItem key={u._id} value={u._id}>{u.name} · {u.role?.replace('_', ' ')}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="text-xs text-[var(--theme-text-muted)]">
                  Employee: <span className="font-medium text-[var(--theme-text)]">{entryDialog.data.user_name || '—'}</span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Clock In</Label>
                  <Input
                    type="datetime-local"
                    value={entryDialog.data.clock_in}
                    onChange={(e) => setEntryDialog({ ...entryDialog, data: { ...entryDialog.data, clock_in: e.target.value } })}
                    data-testid="entry-clock-in"
                  />
                </div>
                <div>
                  <Label className="text-xs">Clock Out</Label>
                  <Input
                    type="datetime-local"
                    value={entryDialog.data.clock_out}
                    onChange={(e) => setEntryDialog({ ...entryDialog, data: { ...entryDialog.data, clock_out: e.target.value } })}
                    data-testid="entry-clock-out"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Break minutes</Label>
                <Input
                  type="number" min="0" step="5"
                  value={entryDialog.data.break_minutes}
                  onChange={(e) => setEntryDialog({ ...entryDialog, data: { ...entryDialog.data, break_minutes: e.target.value } })}
                  data-testid="entry-break"
                />
              </div>
              <div>
                <Label className="text-xs">Notes (optional)</Label>
                <Input
                  value={entryDialog.data.notes}
                  onChange={(e) => setEntryDialog({ ...entryDialog, data: { ...entryDialog.data, notes: e.target.value } })}
                  data-testid="entry-notes"
                />
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => setEntryDialog(null)}>Cancel</Button>
            <Button onClick={saveEntry} className="flex-1 bg-[var(--theme-accent)] text-white" data-testid="entry-save-btn">
              {entryDialog?.mode === 'edit' ? 'Save changes' : 'Add entry'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimesheetPage;
